<template>
  <teleport to="body">
    <WdsModal :show="open" :title="$t('Statement of Work (SoW) engagement')" @cancel="close">
      <WdsStack direction-column gap-md>
        <WdsText>
          <div class="tw-font-semibold">
            {{ $t('Please review all of the following statements apply to this engagement:') }}
          </div>
        </WdsText>

        <WdsStack direction-column gap-md class="tw-p-3 tw-border tw-border-gray-200 tw-rounded-lg">
          <WdsStack v-for="(statement, index) in statements" :key="index" align-start gap-sm>
            <WdsText>
              <span class="tw-font-medium tw-w-5 tw-flex-shrink-0">{{ index + 1 }}.</span>
            </WdsText>
            <WdsText>
              <span>
                {{ statement.text }}
                <WdsTooltip v-if="statement.tooltipText" :text="statement.tooltipText">
                  <span class="tw-text-indigo-500 hover:tw-text-indigo-600 tw-cursor-help">
                    {{ $t('Learn more') }}
                  </span>
                </WdsTooltip>
              </span>
            </WdsText>
          </WdsStack>
        </WdsStack>

        <WdsStack align-center gap-custom="12px" class="tw-mt-2">
          <WdsIcon
            :name="WdsIconNameEnum.CHECKMARK_FILLED"
            :size="20"
            class="tw-text-green-400 tw-flex-shrink-0"
          />
          <WdsText>
            <span class="tw-font-semibold">
              {{
                $t(
                  'I confirm that this is a genuine Statement of Work (SoW) engagement and that all statements above apply.',
                )
              }}
            </span>
          </WdsText>
        </WdsStack>
      </WdsStack>

      <template v-if="!hideFooter" #footer>
        <WdsStack justify-space-between align-center gap-sm class="tw-w-full">
          <WdsButton tertiary @click="close">
            {{ $t('Back') }}
          </WdsButton>

          <WdsStack gap-sm>
            <WdsButton error-subtle @click="handleCancelHire">
              {{ $t('Cancel hire') }}
            </WdsButton>
            <WdsButton success :disabled="isSubmitting" @click="handleConfirm">
              {{ isSubmitting ? $t('Confirming...') : $t('Confirm') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>

    <!-- Cancel Hire Modal - rendered within this component -->
    <CancelHireModal
      :open="showCancelHireModal"
      :hire-id="hireId"
      @close="showCancelHireModal = false"
    />
  </teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsIcon,
  WdsIconNameEnum,
  WdsModal,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import queriesToRefetch from '../../queriesToRefetch'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import CancelHireModal from '@/components/Hire/CancelHireModal.vue'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { ComplianceName } from '@/gql/graphql'
import { useToast } from '@/composables/useToast'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { ComplianceModalProps } from '@/components/Hire/HirePageCompliance/modals/complianceModals'

const toast = useToast()

const props = withDefaults(defineProps<ComplianceModalProps>(), {
  open: false,
  hideFooter: false,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const isSubmitting = ref(false)
const showCancelHireModal = ref(false)

const { mutate: updateCompliance } = useMutation(UpdateComplianceMutation)

const statements = [
  {
    text: 'The work is defined by specific deliverables or milestones, with a clear start and end date.',
    tooltipText:
      'This should be a clearly scoped piece of work with defined dates, not ongoing or open-ended support.',
  },
  {
    text: 'Worker payment is based on completed milestones or deliverables. Time and materials billing, as well as timesheets, are not applicable. This outcome-based engagement cannot be converted to hourly or day-rate work.',
    tooltipText:
      'Payment is triggered only when agreed milestones or deliverables are completed and approved by the client.',
  },
  {
    text: "The worker is an independent contractor, not integrated into the client's organization or subject to supervision, direction or control over how and when the work is performed, provided milestones are met. The worker does not perform ongoing BAU activities.",
  },
  {
    text: 'The worker is responsible for delivering the agreed outcomes and correcting any defective work required to meet the agreed milestones.',
    tooltipText:
      'If changes are needed to meet the milestone, this is addressed through delivery of the agreed work rather than additional time-based charges.',
  },
  {
    text: 'The worker uses their own tools and equipment where reasonably possible, and is free to provide services to other clients. This engagement is non-exclusive.',
  },
]

// Reset state when modal opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      isSubmitting.value = false
    }
  },
)

const close = () => {
  emit('update:open', false)
}

const handleConfirm = async () => {
  isSubmitting.value = true

  try {
    await updateCompliance({
      input: {
        name: ComplianceName.SoWConfirmation,
        data: JSON.stringify({
          confirmed: true,
        }),
        hireId: props.hireId,
      },
    })

    await publicApi.refetchQueries({
      include: queriesToRefetch,
    })

    close()
    toast.success($t('SoW statements confirmed'))
  } catch (error) {
    toast.error($t('An error occurred, please try again.'))
  } finally {
    isSubmitting.value = false
  }
}

const handleCancelHire = () => {
  close()
  showCancelHireModal.value = true
}
</script>

<style scoped lang="scss">
:deep(.wds-modal-body) {
  @apply tw-mt-3;
}
</style>
